class HungaryPlacesSearch {
  private places: any[] | null = null;
  private searchIndex: Record<string, number[]> | null = null;
  private loadingPromise: Promise<void> | null = null;

  async preload(onProgress?: (progress: number) => void): Promise<void> {
    if (this.places) return; // Already loaded

    if (this.loadingPromise) {
      await this.loadingPromise;
      return;
    }

    this.loadingPromise = this.loadDataWithProgress(onProgress);
    await this.loadingPromise;
  }

  private async loadDataWithProgress(
    onProgress?: (progress: number) => void
  ): Promise<void> {
    try {
      onProgress?.(0);

      // Fetch the minified data
      const response = await fetch("/hungary-places-min.json");
      const contentLength = parseInt(
        response.headers.get("Content-Length") || "0"
      );

      if (!response.body) {
        throw new Error("No response body");
      }

      // Read the response with progress tracking
      const reader = response.body.getReader();
      const chunks: Uint8Array[] = [];
      let receivedLength = 0;

      while (true) {
        const { done, value } = await reader.read();

        if (done) break;

        chunks.push(value);
        receivedLength += value.length;

        // Calculate and report progress (0-0.7 for download)
        if (contentLength > 0) {
          const downloadProgress = receivedLength / contentLength;
          onProgress?.(downloadProgress * 0.7);
        }
      }

      onProgress?.(0.7); // Download complete

      // Combine chunks and parse JSON
      const chunksAll = new Uint8Array(receivedLength);
      let position = 0;
      for (const chunk of chunks) {
        chunksAll.set(chunk, position);
        position += chunk.length;
      }

      const jsonString = new TextDecoder("utf-8").decode(chunksAll);
      onProgress?.(0.8); // Decoding complete

      const data = JSON.parse(jsonString);
      onProgress?.(0.9); // Parsing complete

      // Process the minified data
      this.places = this.expandMinifiedData(data);
      this.searchIndex = this.buildSearchIndex(this.places);

      onProgress?.(1); // Processing complete
    } catch (error) {
      console.error("Failed to load places data:", error);
      this.places = [];
      this.searchIndex = {};
      throw error;
    }
  }

  // Ensure loaded before search (no progress tracking)
  private async ensureLoaded(): Promise<void> {
    if (this.places) return;
    await this.preload();
  }

  // Expand minified data to full format
  private expandMinifiedData(data: any): any[] {
    if (!data.p || !Array.isArray(data.p)) {
      throw new Error("Invalid minified data format");
    }

    return data.p.map((p: any) => ({
      id: p.i,
      name: p.n,
      type: p.t,
      lat: p.lt,
      lng: p.ln,
      priority: p.pr || null,
      county: p.c || null,
      districtNumber: p.d || null,
      searchTerms: p.s || [],
    }));
  }

  private buildSearchIndex(places: any[]): Record<string, number[]> {
    const index: Record<string, number[]> = {};

    places.forEach((place, idx) => {
      // Index the main name
      const searchableText = this.normalize(place.name);
      this.addToIndex(index, searchableText, idx);

      // Also index search terms if available
      if (place.searchTerms && Array.isArray(place.searchTerms)) {
        place.searchTerms.forEach((term: string) => {
          const normalizedTerm = this.normalize(term);
          this.addToIndex(index, normalizedTerm, idx);
        });
      }

      // Index district number variations
      if (place.districtNumber) {
        this.addToIndex(index, place.districtNumber.toString(), idx);
        this.addToIndex(index, `${place.districtNumber}ker`, idx);
      }
    });

    return index;
  }

  private addToIndex(
    index: Record<string, number[]>,
    text: string,
    idx: number
  ): void {
    // Index by first 2-3 characters
    for (let len = 2; len <= 3; len++) {
      if (text.length >= len) {
        const key = text.substring(0, len);
        if (!index[key]) index[key] = [];
        if (!index[key].includes(idx)) {
          index[key].push(idx);
        }
      }
    }
  }

  private normalize(text: string): string {
    return text
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "") // Remove accents
      .replace(/[^a-z0-9\s]/g, "") // Remove special chars except spaces
      .trim();
  }

  async search(
    query: string,
    limit: number = 10,
    offset: number = 10
  ): Promise<any[]> {
    await this.ensureLoaded();

    if (!this.places || !query.trim()) return [];

    const normalizedQuery = this.normalize(query);
    const results: Array<{ place: any; score: number }> = [];

    // Special handling for district searches
    const districtMatch = normalizedQuery.match(/^(\d+)\s*ker/);
    if (districtMatch) {
      const districtNum = parseInt(districtMatch[1]);
      const districtResults = this.places.filter(
        (p) => p.districtNumber === districtNum
      );
      if (districtResults.length > 0) {
        return districtResults.slice(0, limit);
      }
    }

    // Use index for faster search
    const indexKeys = this.getIndexKeys(normalizedQuery);
    const candidateIndices = new Set<number>();

    indexKeys.forEach((key) => {
      const indices = this.searchIndex?.[key] || [];
      indices.forEach((idx) => candidateIndices.add(idx));
    });

    // If no candidates from index, search all (for short queries)
    const indicesToSearch =
      candidateIndices.size > 0
        ? Array.from(candidateIndices)
        : normalizedQuery.length < 2
        ? Array.from({ length: this.places.length }, (_, i) => i)
        : [];

    for (const idx of indicesToSearch) {
      const place = this.places[idx];
      const score = this.calculateScore(place, normalizedQuery);

      if (score > 0) {
        results.push({ place, score });
      }
    }

    // Sort by score and return top results
    return results
      .sort((a, b) => b.score - a.score)
      .slice(offset, offset + limit)
      .map((r) => r.place);
  }

  private getIndexKeys(query: string): string[] {
    const keys: string[] = [];

    // Get keys for 2-3 character prefixes
    for (let len = 2; len <= 3; len++) {
      if (query.length >= len) {
        keys.push(query.substring(0, len));
      }
    }

    return keys;
  }

  private calculateScore(place: any, query: string): number {
    const name = this.normalize(place.name);
    let score = 0;

    // Exact match
    if (name === query) return 100;

    // Starts with
    if (name.startsWith(query)) {
      score = 80 - Math.min(20, name.length - query.length);
    }
    // Contains
    else if (name.includes(query)) {
      const position = name.indexOf(query);
      score = 50 - Math.min(20, position);
    }

    // Check search terms if available
    if (place.searchTerms && Array.isArray(place.searchTerms)) {
      for (const term of place.searchTerms) {
        const normalizedTerm = this.normalize(term);
        if (normalizedTerm === query) return 95;
        if (normalizedTerm.startsWith(query)) {
          score = Math.max(score, 70);
        }
        if (normalizedTerm.includes(query)) {
          score = Math.max(score, 40);
        }
      }
    }

    // Boost by priority/type
    if (score > 0 && place.priority) {
      // Lower priority number = more important
      score += Math.max(0, 20 - place.priority);
    }

    return score;
  }

  // Utility method to get stats about loaded data
  getStats(): { total: number; byType: Record<string, number> } | null {
    if (!this.places) return null;

    const byType: Record<string, number> = {};
    this.places.forEach((place) => {
      byType[place.type] = (byType[place.type] || 0) + 1;
    });

    return {
      total: this.places.length,
      byType,
    };
  }
}

// Export singleton instance
export const hungaryPlacesSearch = new HungaryPlacesSearch();
